/**
 * Single source of truth for the owner-only student field lists. Kept in its OWN
 * file with ZERO imports so the firebase-admin netlify sweep can import it without
 * dragging in React/type transitive deps (utils.ts → types.ts → React). Imported
 * by dataService (client split/hydrate/strip) AND sweep-embedded-pii-background.
 */

// Owner-only student fields that must live in classes/{id}/private/roster, NOT in
// the classmate-readable embedded students[] array.
export const STUDENT_PII_FIELDS = [
  'governmentId', 'parentName', 'parentEmail', 'parentPhone',
  'accommodations', 'notes', 'noteHistory', 'parentLinkToken',
  // Stage 0.5a — teacher-only academic working state (reportComment = report-card
  // comment about the student; learningSkills = E/G/S/N; quickAbsences rides along).
  'reportComment', 'learningSkills', 'quickAbsences',
  // Stage 0.5b — teacher-only Hub-average working state (pending draft + offset).
  // studentHubAverage stays embedded (the student reads their own) → readerUids stage.
  'pendingHubAverage', 'studentHubAverageOffset',
  // Stage 0 (readerUids/A′) — the set of auth uids allowed to read this student's
  // grade docs. The reader allow-list must NOT live on the classmate-readable class
  // doc; it's server-managed (arrayUnion at join/login/claim, cleared on password
  // reset) in classes/{id}/private/roster and projected onto each grade's readerUids.
  'authUids',
  // The REAL name a teacher links to a nickname student. The whole point of a nickname class is
  // that the class document carries a pseudonym — and these two fields were sitting on it, so a
  // "Privacy Mode" class published every real name it had been given to all ~30 classmates, which
  // is the opposite of what the mode promises and of what the privacy policy says about it.
  // Teacher-only: every reader is a client-side display helper (Gradebook, Reports, Metrics,
  // Attendance, Seating, Gamification, PathwaysView, AllStudents, utils.sortStudents); nothing in
  // netlify/ reads them, so routing them owner-only costs no server behaviour.
  'realFirstName', 'realLastName',
] as const;

// Deprecated fields DROPPED entirely on every write (not routed to the roster).
// nicknamePasswordHash is a credential now stored server-only in studentAuth; it
// must never re-embed on the classmate-readable class doc from stale client state.
export const STUDENT_DROP_FIELDS = ['nicknamePasswordHash'] as const;

// Owner-only fields that are MERGED BACK onto the in-memory students after stripping, because
// the teacher UI reads them as `student.X`. Only fields with NO updateClass set-path belong
// here, or the release/bulk paths would re-embed or lose them.
//
// authUids is here so the owner's in-memory students carry the reader allow-list: the
// grade-write callers thread it into batchUpdateGrades/releaseGrades without a per-save roster
// read. updateClass strips it again before any class-doc write.
export const MERGE_BACK_TO_STUDENT = [
  'reportComment', 'learningSkills', 'quickAbsences',
  'pendingHubAverage', 'studentHubAverageOffset', 'authUids',
  // Merged back so the ~40 display sites keep reading `student.realFirstName` unchanged. They are
  // written as part of a whole-students array through updateClass (ClassSettingsModal), which
  // strips them again on the way out — there is no set-path for either field.
  'realFirstName', 'realLastName',
] as const;

/**
 * Put those fields back onto a RAW roster array that came off the class doc.
 *
 * Lives here, with the field lists and no imports, for two reasons: the firebase-admin netlify
 * sweep can use it, and a test can exercise it without booting the Firebase SDK — which the
 * first attempt at a test for it could not, because the helper started life in dataService.
 *
 * ANY code path holding a roster array that came off the class doc must run it through here
 * before that array reaches React state. This codebase has skipped that twice. The first time
 * dropped `authUids` and the grade writers then stamped `readerUids: []`, revoking students'
 * access to their own marks. The second was a local-only roster sync that bypassed hydration
 * altogether — same six fields, and a child's seven quick absences written back as one.
 *
 * `roster` is the hydrated private map (`classData.studentPrivate`). A student missing from it
 * is returned untouched.
 */
export const mergeBackStudentPrivate = <T extends { id: string }>(
  students: T[] | undefined,
  // `object` rather than Record<string, unknown>: this file has no imports by design, so it
  // cannot name StudentPrivate, and an interface without an index signature is not assignable
  // to Record<string, unknown>. `object` accepts it and every caller's shape.
  roster: Readonly<Record<string, object>> | undefined,
): T[] => {
  if (!students) return [];
  if (!roster) return students;
  return students.map(s => {
    const priv = roster[s.id] as Record<string, unknown> | undefined;
    if (!priv) return s;
    const merged: Record<string, unknown> = { ...s };
    for (const f of MERGE_BACK_TO_STUDENT) {
      if (priv[f] !== undefined) merged[f] = priv[f];
    }
    return merged as T;
  });
};
